import { useMemo } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions/extension-handler';
import type { ExtensionProvider } from '@atlaskit/editor-common/extensions/extension-provider';
import combineExtensionProviders from '@atlaskit/editor-common/extensions/combine-extension-providers';
import type { ExtensionPluginOptions } from '@atlaskit/editor-plugin-extension/extensionPluginType';

import { expValEquals } from '@confluence/feature-experiments';

import { is1PMacro } from '../extensions-common/macroClassificationUtils.ts';

import { withMigratedMacroRendererView } from './withMigratedMacroRendererView.tsx';

export type useExtensionRendererViewOptionsProps = {
	isLivePage: boolean;
	contentId: string;
	spaceKey: string;
	getExtensionHandlers?: (options: any) => ExtensionHandlers;
	getHeadingLinkPath?: () => string | null;
	/**
	 * Editor extension providers. Used to render Connect macros migrated to Forge (h11n) through
	 * the provider instead of the legacy renderer view.
	 */
	extensionProviders?: (ExtensionProvider | Promise<ExtensionProvider>)[];
};

export const is1PBodiedExtension = ({
	extensionKey,
	extensionType,
}: {
	extensionKey?: string;
	extensionType?: string;
}): boolean => is1PMacro(extensionKey || '') && extensionType === 'bodiedExtension';

export const is2PBodiedExtension = ({
	extensionKey,
	extensionType,
}: {
	extensionKey?: string;
	extensionType?: string;
}): boolean => !is1PMacro(extensionKey || '') && extensionType === 'bodiedExtension';

export const useExtensionRendererViewOptions = ({
	getExtensionHandlers,
	isLivePage,
	contentId,
	spaceKey,
	getHeadingLinkPath,
	extensionProviders,
}: useExtensionRendererViewOptionsProps): ExtensionPluginOptions['__rendererExtensionOptions'] => {
	const combinedExtensionProvider = useMemo(
		() =>
			isLivePage && extensionProviders?.length
				? combineExtensionProviders(extensionProviders)
				: undefined,
		[isLivePage, extensionProviders],
	);

	return useMemo(() => {
		if (isLivePage) {
			const rendererExtensionHandlers = getExtensionHandlers?.({
				contentId,
				spaceKey,
				isInEditorRendererView: true,
				getHeadingLinkPath,
			});
			return {
				rendererExtensionHandlers: expValEquals(
					'confluence_live_page_migrated_bodied_macro_h11n',
					'isEnabled',
					true,
				)
					? withMigratedMacroRendererView(rendererExtensionHandlers, combinedExtensionProvider)
					: rendererExtensionHandlers,
				isAllowedToUseRendererView: (node: ADFEntity) => {
					return is2PBodiedExtension({
						extensionKey: node.attrs?.extensionKey,
						extensionType: node.type,
					});
				},
				showUpdated1PBodiedExtensionUI: (node: ADFEntity) =>
					is1PBodiedExtension({
						extensionKey: node.attrs?.extensionKey,
						extensionType: node.type,
					}),
			};
		}
		return undefined;
	}, [
		getExtensionHandlers,
		isLivePage,
		contentId,
		spaceKey,
		getHeadingLinkPath,
		combinedExtensionProvider,
	]);
};
